/**
 * GFG-273 (Campaigns tab, GFG Admin). React Query hooks for `/admin/campaigns`, consumed by the
 * Campaigns panel. Key shape and staleness conventions mirror hooks/useSendQueueCampaigns.ts.
 *
 * No mutation applies an optimistic update: every one of the five invalidates the
 * `["admin", "campaigns"]` prefix on success only, so a mutation the server refuses leaves the row
 * exactly as it was (SPEC B2) and a mutation it accepts refreshes the table with no manual reload.
 * Resulting statuses are read off the mutation response — the server owns what a resume restores,
 * the UI never derives it. Errors stay on the mutation result for the panel to surface.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  archiveAdminCampaign,
  fetchAdminCampaigns,
  pauseAdminCampaign,
  postAdminCampaignStatus,
  resumeAdminCampaign,
} from "@/api/adminCampaigns";
import type { AdminCampaignsListParams } from "@/interfaces/adminCampaigns";

/** Every query this tab owns hangs off one prefix, so a single call invalidates all of them. */
const ADMIN_CAMPAIGNS_KEY = ["admin", "campaigns"] as const;

/** GET /admin/campaigns, keyed by every param so distinct filters/sorts cache independently. */
export function useAdminCampaigns(params?: AdminCampaignsListParams) {
  return useQuery({
    queryKey: [...ADMIN_CAMPAIGNS_KEY, "list", params],
    queryFn: () => fetchAdminCampaigns(params),
  });
}

/**
 * The row menu's three campaign actions plus the contacts modal's two per-recipient actions. All
 * five share one rule: no optimistic update, invalidate on success.
 */
export function useAdminCampaignActions() {
  const queryClient = useQueryClient();

  // The campaign's open dialogs read its status from their own queries, so those refetch too.
  function invalidateAdminCampaigns(_data: unknown, id: number) {
    queryClient.invalidateQueries({ queryKey: ADMIN_CAMPAIGNS_KEY });
    queryClient.invalidateQueries({ queryKey: ["campaignContacts", String(id)] });
  }

  const pause = useMutation({
    mutationFn: (id: number) => pauseAdminCampaign(id),
    onSuccess: invalidateAdminCampaigns,
  });

  const resume = useMutation({
    mutationFn: (id: number) => resumeAdminCampaign(id),
    onSuccess: invalidateAdminCampaigns,
  });

  const archive = useMutation({
    mutationFn: (id: number) => archiveAdminCampaign(id),
    onSuccess: invalidateAdminCampaigns,
  });

  const cancel = useMutation({
    mutationFn: (id: number) => postAdminCampaignStatus(id, "cancel"),
    onSuccess: invalidateAdminCampaigns,
  });

  const restore = useMutation({
    mutationFn: (id: number) => postAdminCampaignStatus(id, "restore"),
    onSuccess: invalidateAdminCampaigns,
  });

  const disable = useMutation({
    mutationFn: (id: number) => postAdminCampaignStatus(id, "disable"),
    onSuccess: invalidateAdminCampaigns,
  });

  const enable = useMutation({
    mutationFn: (id: number) => postAdminCampaignStatus(id, "enable"),
    onSuccess: invalidateAdminCampaigns,
  });

  return { pause, resume, cancel, restore, disable, enable, archive };
}
